import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ScoutAvatar from "../scout-request/ScoutAvatar";
import { SourceLabelPill } from "../scout-request/ScoutPills";
import { formatScoutPosition } from "../../constants/scoutRequestChoices";
import { formatScoutDate, formatScoutHorizon } from "../../utils/scoutFormat";

// Recommendation-Forward chat card (design 5e, CLUB forwards a radar player).
// Rendered from the `scout_recommendation_forward` attachment (contract §2).
// FORWARD-SCOPING is server-decided and expressed by presence: when every channel
// member is a club member the payload carries `attribution` + `source_label` +
// `cta_path` (internal card); forwarded to a channel with a non-club member those
// keys are ABSENT and the card is the neutral player card only. The FE FEATURE-
// DETECTS (`'attribution' in meta`) — it never assumes the internal keys exist.
const ScoutRecommendationForwardCard = (props) => {
  const navigate = useNavigate();
  const meta = props.attachments?.[0]?.metadata || {};
  const player = meta.player || {};
  const forSpec = meta.for || {};
  const isInternal = "attribution" in meta; // scoping signal, per contract

  const playerMeta = [formatScoutPosition(player.position), player.age, player.club]
    .filter((v) => v !== null && v !== undefined && v !== "")
    .join(" · ");

  return (
    <div className="max-w-[340px] bg-white border border-[#E5E7EB] rounded-xl p-4 shadow-[0_1px_2px_rgba(17,24,39,0.05)]">
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#9CA3AF] mb-2">
        {meta.label || "From the radar"}
      </p>

      <div className="flex items-start gap-2.5">
        <ScoutAvatar name={player.name} size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[14px] font-semibold text-[#111827]">{player.name}</p>
            {isInternal && meta.source_label && <SourceLabelPill label={meta.source_label} />}
          </div>
          {playerMeta && <p className="text-[12px] text-[#6B7280] mt-0.5">{playerMeta}</p>}
        </div>
      </div>

      {(forSpec.position_primary || forSpec.horizon) && (
        <p className="text-[12px] text-[#6B7280] mt-3">
          For: <span className="font-semibold text-[#374151]">{formatScoutPosition(forSpec.position_primary)}</span>
          {forSpec.horizon ? ` · ${formatScoutHorizon(forSpec.horizon)}` : ""}
        </p>
      )}

      {/* Internal-only: permanent attribution + "View on radar" CTA. */}
      {isInternal && meta.attribution && (
        <p className="text-[11.5px] text-[#9CA3AF] mt-2">
          Recommended by {meta.attribution.agent || "Former member"}
          {meta.attribution.agency ? ` · ${meta.attribution.agency}` : ""}
          {meta.attribution.at ? ` · ${formatScoutDate(meta.attribution.at)}` : ""}
        </p>
      )}

      {isInternal && meta.cta_path && (
        <button
          type="button"
          onClick={() => navigate(meta.cta_path)}
          className="mt-3.5 w-full flex items-center justify-center gap-1.5 rounded-lg border border-[#E5E7EB] text-[#111827] text-[12.5px] font-semibold py-2 hover:bg-gray-50"
        >
          View on radar
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default ScoutRecommendationForwardCard;
